import React, { useState } from 'react';
import { MenuItem, Popover, useMediaQuery } from '@mui/material';
import { Button, Modal } from 'flowbite-react';
import { IoChevronDown } from 'react-icons/io5';
import classNames from 'classnames';
import { useThemeContext } from 'src/context/ThemeProvider';
import { MenuOptionsType } from 'src/components/recruiterPositions/RecruiterCandidateCardFooter';
import RemarksDrawer from 'src/components/my-applications/application-card-v2/RemarksDrawer';
import { ApplicationStatus, UIOfflineDriveRoundDetail, UIRemarksDrawerType } from 'types';
import { useOfflineDriveRejectRound } from 'src/hooks/utils/mutations/offlineDriveMutations/useOfflineDriveRejectRound.hooks';
import { useUndoOfflineDriveLastRound } from 'src/hooks/utils/mutations/offlineDriveMutations/useUndoOfflineDriveLastRound.hooks';
import { useToast } from 'src/context/ToastProvider';
import { useQueryClient } from 'react-query';
import RecruiterCandidateSendOfferLetter from 'src/components/recruiterPositions/RecruiterCandidateSendOfferLetter';
import { Drawer } from 'src/components/shared/Drawer';
const OpportunityActions = ({
	driveId,
	isUserPlaced,
	applicationRound,
	showUndoAssign = false,
}: {
	driveId: string;
	isUserPlaced?: boolean;
	applicationRound: UIOfflineDriveRoundDetail;
	showUndoAssign?: boolean;
}) => {
	const isMobile = useMediaQuery('(max-width: 960px)');
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const id = open ? 'simple-popover' : undefined;
	const [remarks, setRemarks] = useState<string>('');
	const [remarksDrawerCtaText, setRemarksDrawerCtaText] = useState<string>('Submit');
	const [remarksDrawerType, setRemarksDrawerType] = useState<UIRemarksDrawerType>(
		UIRemarksDrawerType.REJECT_ASSIGNMENT,
	);
	const [openActionDrawer, setOpenActionDrawer] = useState(false);
	const [actionDrawerHeading, setActionDrawerHeading] = useState<string | React.ReactNode>('');
	const [selectedActionKey, setSelectedActionKey] = useState<string>('');
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const isPlaced = [ApplicationStatus.PLACED, ApplicationStatus.VERBALLY_PLACED].includes(
		applicationRound.application?.status,
	);

	const toast = useToast();
	const queryClient = useQueryClient();

	const offlineDriveRejectRoundMutation = useOfflineDriveRejectRound({
		onComplete: () => {
			queryClient.invalidateQueries(['getOfflineDriveCandidates', driveId]);
			queryClient.refetchQueries(['getOfflineDriveCandidates', driveId]);
			setOpenActionDrawer(false);

			toast({
				title: 'Round rejected successfully',
			});
		},
	});

	const undoMutation = useUndoOfflineDriveLastRound({ driveId });

	const menuOptions: MenuOptionsType[] = [
		{
			label: 'Undo assign',
			key: 'undo-assign',
			hide: !showUndoAssign || isPlaced || isUserPlaced,
			disabled: undoMutation.isLoading,
			onClick: () => {
				if (
					!window.confirm(
						'Undo this assignment? The latest round for this position will be removed and the application will return to its previous status (e.g. Shortlisted).',
					)
				) {
					return;
				}
				undoMutation.mutate({
					userId: applicationRound.user._id || '',
					positionId: applicationRound.position._id || '',
					applicationRoundId: applicationRound.roundId || '',
				});
			},
		},
		{
			label: 'Reject Round',
			key: 'reject',
			hide: isPlaced,
			onClick: () => {
				setActionDrawerHeading(
					<div className="px-4 pt-3">
						<h1 className="text-[#1C1C28] dark:text-inherit text-xl md:text-2xl font-inter font-[500]">Add Remarks</h1>
						<p className="text-red-500 dark:text-inherit text-xs font-inter font-[500] italic mt-2">
							Note: Candidates will get rejected in this position and all rounds of this position will get closed.
						</p>
					</div>,
				);
				setSelectedActionKey('reject');
				setOpenActionDrawer(true);
				setRemarksDrawerCtaText('Reject Round');
				setRemarksDrawerType(UIRemarksDrawerType.REJECT_ASSIGNMENT);
			},
		},
		{
			label: 'Send Offer',
			key: 'sendOffer',
			hide: isPlaced,
			onClick: () => {
				setActionDrawerHeading(
					<div className="px-4 pt-3">
						<h1 className="text-[#1C1C28] dark:text-inherit text-xl md:text-2xl font-inter font-[500]">
							Send offer letter:{' '}
						</h1>
						<p className="text-red-500 dark:text-inherit text-sm font-inter font-[500] "></p>
					</div>,
				);
				setSelectedActionKey('sendOffer');
				setOpenActionDrawer(true);
			},
		},
		{
			label: 'Update Offer',
			key: 'sendOffer',
			hide: !isPlaced,
			onClick: () => {
				setActionDrawerHeading(
					<div className="px-4 pt-3">
						<h1 className="text-[#1C1C28] dark:text-inherit text-xl md:text-2xl font-inter font-[500]">
							Update offer letter:{' '}
						</h1>
						<p className="text-red-500 dark:text-inherit text-sm font-inter font-[500] "></p>
					</div>,
				);
				setSelectedActionKey('sendOffer');
				setOpenActionDrawer(true);
			},
		},
	];

	const handleSubmit = () => {
		offlineDriveRejectRoundMutation.mutate({
			userIds: [applicationRound.user._id || ''],
			positionId: applicationRound.position._id || '',
			remarks,
		});
	};

	const getActionButtonContent = (setSelectedActionKeyData: string) => {
		switch (setSelectedActionKeyData) {
			case 'reject':
				return (
					<RemarksDrawer
						remarks={remarks}
						setRemarks={setRemarks}
						isLoading={offlineDriveRejectRoundMutation.isLoading}
						ctaText={remarksDrawerCtaText}
						type=""
						handleSubmit={handleSubmit}
						remarksDrawerType={remarksDrawerType}
					/>
				);
			case 'sendOffer':
				return (
					<RecruiterCandidateSendOfferLetter
						userName={applicationRound?.user?.name}
						adminRejectionRemarks={applicationRound?.application?.adminRejectionRemarks}
						adminRejectedOfferLetter={applicationRound?.application?.adminRejectedOfferLetter}
						offeredPositionParameters={applicationRound.application?.offeredPositionParameters}
						offerLetterLink={applicationRound.application?.offerLetterLink}
						applicationId={applicationRound?.application?._id}
						positionId={applicationRound.position._id ?? ''}
						onFinish={() => {
							queryClient.invalidateQueries({ queryKey: ['getOfflineDriveCandidates', driveId] });
							queryClient.refetchQueries({ queryKey: ['getOfflineDriveCandidates', driveId] });
							setOpenActionDrawer(false);
						}}
					/>
				);
			default:
				return 'Take Action';
		}
	};

	return (
		<>
			<Button
				color="purplePlaced"
				disabled={(isUserPlaced && !isPlaced) || applicationRound.application?.pendingForAdminApproval}
				className="whitespace-pre [&_span]:!p-0 !p-0 border-[#E5E7EB]"
				onClick={handleClick}
			>
				<p className="px-[10px] py-[8px]">
					{applicationRound?.application?.adminRejectedOfferLetter
						? 'Revise Offer'
						: applicationRound.application?.pendingForAdminApproval
						  ? 'In Review'
						  : 'Take Action'}
				</p>
				<IoChevronDown
					className={classNames('w-[34px] h-[34px] px-[8px] transition-transform', open ? 'rotate-[180deg]' : '')}
				/>
			</Button>

			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				sx={{
					'&>.MuiPaper-root': {
						borderRadius: '8px',
						background: 'white',
						boxShadow: '0px 1px 2px -1px rgba(0, 0, 0, 0.10), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)',
					},
					'&>.MuiPaper-root>.MuiButtonBase-root': {
						minHeight: '40px',
					},
				}}
			>
				{menuOptions &&
					menuOptions
						?.filter(menuOption => !menuOption.hide)
						?.map((menuOption: MenuOptionsType, index: number) => (
							<MenuItem
								key={index}
								disabled={menuOption.disabled}
								className={'dark:!text-[#C7C9D9] dark:hover:!bg-[#2c3c50]'}
								style={{
									padding: '8px 16px',
								}}
								onClick={event => {
									if (menuOption.disabled) return;
									if (menuOption.onClick) {
										menuOption.onClick(event);
									}
									handleClose();
								}}
							>
								{menuOption.label}
							</MenuItem>
						))}
			</Popover>

			<Drawer
				fullWidth
				isModal={!isMobile}
				isOpen={openActionDrawer}
				onClose={() => setOpenActionDrawer(false)}
				maxHeight="90vh"
				minHeight="auto"
				boxShadow="0px -8px 32px 0px #00000040"
				header={actionDrawerHeading}
				body={<div className="px-4 py-2">{getActionButtonContent(selectedActionKey)}</div>}
			/>
		</>
	);
};

export default OpportunityActions;
